import { User } from "@supabase/supabase-js";
import { makeAutoObservable, toJS } from "mobx";
import { wrap } from "../util/supabase-wrapper";
import { supabase, definitions } from "./supabase/client";
import { CONSTANTS } from "../CONSTANTS";
import { Child } from "./child";
import { globalStores } from "./globalStores";
import backendFetch from "./customFetch/customFetch";

export class Parent {
  user: Partial<User> = {};
  parentDetail: Partial<definitions["ParentDetails"]> = {};
  userDetail: Partial<definitions["UserDetails"]> = {};
  parentAccount: Partial<definitions["ParentAccount"]> = {};

  constructor() {
    makeAutoObservable(this);
    this.pullUserDetails();
  }

  getReferralCode() {
    if (!this.parentAccount.account) {
      throw new Error("Parent account not properly setup yet!");
    }
    return this.parentAccount.account;
  }

  async signOut() {
    await supabase.auth.signOut();
    this.user = {};
    this.setParentAccount({});
    this.setUserDetail({});
    this.setParentAccount({});
    return this;
  }

  async getPaymentDetails() {
    const childIDs = await this.getChildrenIDs();
    const paymentDetails = wrap(
      await supabase.from("PaymentStatus").select("*").in("account", childIDs)
    );

    return paymentDetails;
  }

  private async getChildrenIDs() {
    const supabaseRaw = await supabase.from("ChildAccount").select("id").match({
      parentAccount: this.parentAccount.id,
    });
    const result = wrap(supabaseRaw);

    return result.map(({ id }) => id);
  }

  async getChildren() {
    const childrenIDs = await this.getChildrenIDs();

    const children = Promise.all(
      childrenIDs.map(async (id) => {
        const child = new Child();
        await child.init(id);
        return child;
      })
    );

    return children;
  }

  async emailSignUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    referralCode?: string
  ) {
    //TODO: Add rollback if failed
    const {
      data: { user, session },
      error,
    } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${process.env.REACT_APP_FRONTEND_URL}/email-confirmed`,
      },
    });
    if (error) throw error;
    if (!user) throw new Error("Sign up failed!");

    this.user = user;

    //check if the email being used for signing up already belongs to an existing user in UserDetails table.
    let existingUser = await supabase
      .from("ParentDetails")
      .select("*")
      .match({ email });

    //if so, we'll take his first and last name
    if (existingUser.data?.length && existingUser.data.length > 0) {
      const resultUser = wrap(
        await supabase
          .from("UserDetails")
          .select("*")
          .match({ firstName, lastName })
      );

      const resultParent = wrap(existingUser);

      this.parentDetail = resultParent[0];
      this.userDetail = resultUser[0];

      const resultAccount = wrap(
        await supabase.from("ParentAccount").select("*").match({
          parentDetail: this.parentDetail.id,
          userDetail: this.userDetail.id,
          account: user.id,
        })
      );

      this.parentAccount = resultAccount[0];
    } else {
      const resultUser = wrap(
        await supabase
          .from("UserDetails")
          .insert({
            firstName,
            lastName,
            referralCode,
          })
          .select()
      );

      const parentDetail: Partial<definitions["ParentDetails"]> = {
        email,
      };

      const resultParent = wrap(
        await supabase.from("ParentDetails").insert([parentDetail]).select()
      );

      this.parentDetail = resultParent[0];
      this.userDetail = resultUser[0];

      const resultAccount = wrap(
        await supabase
          .from("ParentAccount")
          .insert({
            parentDetail: this.parentDetail.id,
            userDetail: this.userDetail.id,
            account: user.id,
          })
          .select()
      );

      this.parentAccount = resultAccount[0];
    }

    return this;
  }

  async emailSignIn(email: string, password: string) {
    const {
      data: { user },
      error,
    } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    //TODO: Replace with login failed depending on user creation
    if (!user) throw new Error("User creation failed!");

    this.user = user;

    //TODO: Instead of pulling user details can listen for DB updates
    await this.pullUserDetails();

    if (this.parentAccount?.id === undefined) {
      throw new Error("Unable to sign in!");
    }

    return this;
  }

  async acceptPrivacyPolicy() {
    if (!this.parentDetail)
      throw new Error(
        "Parent details need to exist to accept the privacy policy!"
      );

    await supabase
      .from("ParentDetails")
      .update({ privacyPolicyAccepted: true })
      .match({ id: this.parentDetail.id })
      .throwOnError();

    await this.pullUserDetails();
  }

  async submitMetaData(
    originCountry: string,
    city: string,
    contactNumber: string
  ) {
    //TODO: Input validation (Best is to validate on database level)
    if (!this.parentDetail)
      throw new Error("Parent details need to exist to submit metadata!");
    if (!this.userDetail)
      throw new Error("Parent details need to exist to submit metadata!");
    await supabase
      .from("ParentDetails")
      .update({ contactNumber: contactNumber })
      .match({ id: this.parentDetail.id })
      .throwOnError();
    await supabase
      .from("UserDetails")
      .update({
        originCountry,
        city,
      })
      .match({ id: this.userDetail.id })
      .throwOnError();
    await this.pullUserDetails();
  }

  async createChild(
    firstName: string,
    lastName: string,
    birthday: Date,
    passCode: number,
    city: string,
    childSchool: string
  ) {
    //Check that the maximum number of accounts for the parent has not been exceeded
    const numParentChilds = wrap(
      await supabase
        .from("ChildAccount")
        .select("id")
        .match({ parentAccount: this.parentAccount.id })
    );

    if (numParentChilds.length >= CONSTANTS.MAX_CHILD_ACCOUNTS) {
      throw new Error(
        `Parent accounts can only have up to ${CONSTANTS.MAX_CHILD_ACCOUNTS}`
      );
    }

    //TODO: Proper rollback after insertion
    //TODO: Input validation
    const childDetail: Partial<definitions["ChildDetails"]> = {
      passCode,
      childSchool,
    };

    const resultChild = wrap(
      await supabase.from("ChildDetails").insert([childDetail]).select()
    );

    const resultUser = wrap(
      await supabase
        .from("UserDetails")
        .insert({
          referralCode: this.userDetail.referralCode,
          originCountry: this.userDetail.originCountry,
          city,
          firstName,
          lastName,
          birthday: birthday.toISOString(),
        })
        .select()
    );

    const talkingPartyStatus = wrap(
      await supabase.from("TalkingPartyStatus").insert({}).select()
    );

    const child: Partial<definitions["ChildAccount"]> = {
      childDetail: resultChild[0].id,
      userDetail: resultUser[0].id,
      parentAccount: this.parentAccount.id,
      talkingPartyStatus: talkingPartyStatus[0].id,
    };

    const result = wrap(
      await supabase.from("ChildAccount").insert(child).select()
    );

    await this.pullUserDetails();

    const newlyCreatedChildInstance = await globalStores.childStore.init(
      result[0].id
    );
    // if this is the first time the parent creates a child, send referral code email.
    if (numParentChilds.length === 0) {
      backendFetch("email/send-referral-code-email", "POST", {
        parentId: this.parentAccount.id,
      });
    }

    //automatically join free trial
    await newlyCreatedChildInstance.joinFreeTrial();
    return this;
  }

  private setParentAccount(newParentAccount: typeof this.parentAccount) {
    this.parentAccount = newParentAccount;
  }

  private setParentDetail(newParentDetail: typeof this.parentDetail) {
    this.parentDetail = newParentDetail;
  }

  private setUserDetail(newUserDetail: typeof this.userDetail) {
    this.userDetail = newUserDetail;
  }

  private async pullUserDetails() {
    const userMeta = await supabase.auth.getUser();
    if (!userMeta.data.user) return;
    this.user = userMeta.data.user;
    //TODO: Pull all at once to reduce lag
    this.setParentAccount(
      wrap(
        await supabase
          .from("ParentAccount")
          .select("*")
          .match({ account: this.user.id })
      )[0]
    );
    if (this.parentAccount) {
      this.setParentDetail(
        wrap(
          await supabase
            .from("ParentDetails")
            .select("*")
            .match({ id: this.parentAccount.parentDetail })
        )[0]
      );
      this.setUserDetail(
        wrap(
          await supabase
            .from("UserDetails")
            .select("*")
            .match({ id: this.parentAccount.userDetail })
        )[0]
      );
    }

    return this;
  }
}

export default new Parent();
